import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNav from './StepNav';
import {
  syncSummary,
  timelineTracks,
  timelineShots,
  syncStatusMeta,
  syncTable,
  syncDetailByShot,
  defaultSyncShotId,
  canDrilldown,
} from '../../mock/moduleb';
import './moduleb-common.css';
import './ModuleBShotSync.css';

const STAT_CARDS = [
  { key: 'totalShots', labelKey: 'pages.moduleb.sync.stats.totalShots', icon: 'stacks', accent: '#3B82F6', value: syncSummary.totalShots.toLocaleString(), unit: '' },
  { key: 'matchRate', labelKey: 'pages.moduleb.sync.stats.matchRate', icon: 'link', accent: '#22c55e', value: syncSummary.matchRate, unit: '%' },
  { key: 'confidence', labelKey: 'pages.moduleb.sync.stats.confidence', icon: 'verified', accent: '#f97316', value: syncSummary.avgConfidence, unit: '%' },
  { key: 'mismatch', labelKey: 'pages.moduleb.sync.stats.mismatch', icon: 'link_off', accent: '#ef4444', value: syncSummary.mismatchCount, unitKey: 'pages.moduleb.common.unitCount' },
];

// 타임라인 x축 범위
const T_MAX = 36;

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const meta = syncStatusMeta[status];
  return (
    <span className="mb-badge" style={{ color: meta.color, background: meta.bg }}>
      {t(meta.labelKey)}
    </span>
  );
}

function ModuleBShotSync() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedShot, setSelectedShot] = useState(defaultSyncShotId);
  const detail = syncDetailByShot[selectedShot] || syncDetailByShot[defaultSyncShotId];
  const drilldownAllowed = canDrilldown(selectedShot);

  const handleDrilldown = () => {
    if (!drilldownAllowed) return;
    navigate(`/module-b/features?shot=${selectedShot}`);
  };

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNav current={1} shotId={selectedShot} />

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag">STEP 1</span>{t('pages.moduleb.nav.steps.sync.title')}
            </div>
            <div className="mb-page-subtitle">
              {t('pages.moduleb.sync.subtitle')}
            </div>
          </div>
          <div className="mb-page-meta">
            {t('pages.moduleb.sync.meta')}
          </div>
        </div>

        <div className="mb-body">
          {/* 요약 스탯 4개 */}
          <div className="mb-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {STAT_CARDS.map((c) => (
              <div key={c.key} className="mb-stat-card" style={{ '--mb-accent': c.accent }}>
                <div className="mb-stat-label">
                  <span className="material-symbols-outlined" style={{ color: c.accent }}>{c.icon}</span>
                  {t(c.labelKey)}
                </div>
                <div className="mb-stat-value">
                  {c.value}
                  {(c.unit || c.unitKey) && <span className="mb-stat-unit">{c.unitKey ? t(c.unitKey) : c.unit}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* 타임라인 */}
          <div className="mb-card">
            <div className="mb-card-title">
              <span className="material-symbols-outlined">timeline</span>
              {t('pages.moduleb.sync.timelineTitle')}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>
                {t('pages.moduleb.sync.timelineHint')}
              </span>
            </div>

            <div className="sync-timeline">
              {/* 트랙 라벨 */}
              <div className="sync-track-labels">
                {timelineTracks.map((tr) => (
                  <div key={tr.source} className="sync-track-label">
                    <span className="sync-dot" style={{ background: tr.color }} />
                    {t(tr.labelKey)}
                  </div>
                ))}
              </div>

              {/* 트랙 영역 */}
              <div className="sync-track-area">
                {/* 세로 매칭선 (동일 제품 묶음) */}
                {timelineShots.map((shot) => {
                  const leftPct = (shot.t / T_MAX) * 100;
                  return (
                    <div
                      key={`link-${shot.shotId}`}
                      className={`sync-align-line ${shot.status} ${selectedShot === shot.shotId ? 'active' : ''}`}
                      style={{ left: `${leftPct}%` }}
                      title={`${shot.shotId} · ${shot.productId}`}
                    >
                      <span className="sync-align-tag">{shot.productId}</span>
                    </div>
                  );
                })}

                {/* 3개 트랙 라인 + 마커 */}
                {timelineTracks.map((tr) => (
                  <div key={tr.source} className="sync-track-row">
                    <div className="sync-track-baseline" />
                    {timelineShots.map((shot) => {
                      const leftPct = (shot.t / T_MAX) * 100;
                      const unmatched = !shot.matched[tr.source];
                      return (
                        <button
                          key={`${tr.source}-${shot.shotId}`}
                          type="button"
                          className={`sync-marker ${unmatched ? 'missing' : ''} ${selectedShot === shot.shotId ? 'active' : ''}`}
                          style={{ left: `${leftPct}%`, borderColor: tr.color, background: unmatched ? 'transparent' : tr.color }}
                          onClick={() => setSelectedShot(shot.shotId)}
                          title={`${t(tr.labelKey)} ${unmatched ? t('pages.moduleb.sync.unmatched') : t('pages.moduleb.sync.matched')}`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* x축 */}
              <div className="sync-axis">
                {[0, 6, 12, 18, 24, 30, 36].map((s) => (
                  <span key={s} className="sync-axis-tick" style={{ left: `${(s / T_MAX) * 100}%` }}>{s}s</span>
                ))}
              </div>
            </div>
          </div>

          {/* 테이블 + 매칭 상세 */}
          <div className="mb-grid mb-grid-2-1 mb-mt">
            <div className="mb-card" style={{ padding: '16px 16px 8px' }}>
              <div className="mb-card-title">
                <span className="material-symbols-outlined">table_rows</span>
                {t('pages.moduleb.sync.tableTitle')}
              </div>
              <div className="mb-table-wrap" style={{ maxHeight: 360 }}>
                <table className="mb-table">
                  <thead>
                    <tr>
                      <th>{t('pages.moduleb.common.shotId')}</th>
                      <th>{t('pages.moduleb.common.productId')}</th>
                      <th style={{ textAlign: 'center' }}>{t('pages.moduleb.common.sources.process')}</th>
                      <th style={{ textAlign: 'center' }}>{t('pages.moduleb.common.sources.vision')}</th>
                      <th style={{ textAlign: 'center' }}>{t('pages.moduleb.common.sources.thermal')}</th>
                      <th style={{ textAlign: 'right' }}>{t('pages.moduleb.sync.confidence')}</th>
                      <th>{t('pages.moduleb.sync.judgement')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncTable.map((row) => (
                      <tr
                        key={row.shotId}
                        className={selectedShot === row.shotId ? 'selected' : ''}
                        onClick={() => setSelectedShot(row.shotId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="mb-mono">{row.shotId}</td>
                        <td className="mb-mono">{row.productId}</td>
                        <td className="mb-td-check">{row.process ? <span className="mb-check-yes">●</span> : <span className="mb-check-no">○</span>}</td>
                        <td className="mb-td-check">{row.vision ? <span className="mb-check-yes">●</span> : <span className="mb-check-no">○</span>}</td>
                        <td className="mb-td-check">{row.thermal ? <span className="mb-check-yes">●</span> : <span className="mb-check-no">○</span>}</td>
                        <td className="mb-mono" style={{ textAlign: 'right', color: row.confidence >= 90 ? '#22c55e' : row.confidence >= 70 ? '#fbbf24' : '#ef4444' }}>
                          {row.confidence.toFixed(1)}%
                        </td>
                        <td><StatusBadge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 매칭 상세 패널 */}
            <div className="mb-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">center_focus_strong</span>
                {t('pages.moduleb.sync.detailTitle')}
              </div>
              <div className="sync-detail-shot">
                <span className="sync-detail-shot-id">{selectedShot}</span>
                <span className="material-symbols-outlined sync-detail-arrow">arrow_forward</span>
                <span className="sync-detail-product">{detail.productId}</span>
              </div>

              <div className="sync-detail-ref">
                <span className="material-symbols-outlined">inventory_2</span>
                <div>
                  <div className="sync-detail-ref-label">{t('pages.moduleb.sync.refLabel')}</div>
                  <div className="sync-detail-ref-time">{detail.productId} · {detail.reference}</div>
                </div>
              </div>

              <div className="mb-section-label mb-mt">{t('pages.moduleb.sync.sourceKeyLabel')}</div>
              <div className="mb-kv-list">
                {detail.sources.map((s) => (
                  <div key={s.source} className={`sync-detail-src ${!s.matched ? 'unmatched' : ''}`}>
                    <div className="sync-detail-src-head">
                      <span className="sync-dot" style={{ background: timelineTracks.find(t => t.source === s.source)?.color }} />
                      {t(s.labelKey)}
                      <span className="sync-detail-src-key">{s.key}{s.keySuffixKey ? t(s.keySuffixKey) : ''}</span>
                    </div>
                    <div className={`sync-detail-src-conf ${s.matched ? 'ok' : 'no'}`}>
                      {s.matched ? (
                        <><span className="material-symbols-outlined">check_circle</span>{s.confidence.toFixed(1)}%</>
                      ) : (
                        <><span className="material-symbols-outlined">cancel</span>{t('pages.moduleb.sync.unmatched')}</>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="sync-detail-note">
                <span className="material-symbols-outlined">info</span>
                {t('pages.moduleb.sync.detailNote')}
              </div>

              {/* 드릴다운 CTA */}
              <button
                type="button"
                className={`sync-drilldown-btn ${drilldownAllowed ? '' : 'disabled'}`}
                onClick={handleDrilldown}
                disabled={!drilldownAllowed}
              >
                {drilldownAllowed ? (
                  <>
                    <span>{t('pages.moduleb.sync.drilldown', { product: detail.productId })}</span>
                    <span className="material-symbols-outlined">arrow_forward</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">block</span>
                    <span>{t('pages.moduleb.sync.drilldownBlocked')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default ModuleBShotSync;
